import { type ReactNode, useState } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { triggerHaptic } from '@repo/shared/client';
import { errorTracker } from '@repo/shared/common';
import { sessionQueryOptions, sessionQueryKey } from '~/lib/queries/session';
import {
  currentOrganizationQueryOptions,
  currentOrganizationQueryKey,
  myOrganizationsQueryKey,
  myInvitationsQueryKey,
  myInvitationsQueryOptions,
  useAcceptInvitation,
  useDeclineInvitation,
} from '~/lib/queries/organization';
import { PORTFOLIO_KEY } from '~/features/portfolio/use-portfolio';
import { resolveLandingRoute } from '~/lib/landing';
import { markOnboardingComplete } from '~/lib/stores/onboarding-celebration';
import { Stack, Text, Reveal } from '~/components/ui';
import { Mail } from '~/components/icons';
import { OnboardingHeader } from '~/components/onboarding/OnboardingHeader';
import { InvitationRow, type InvitationBusyAction } from '~/components/organizations/InvitationRow';
import styles from '~/components/onboarding/onboarding-step.module.css';

/**
 * Onboarding "you've been invited" step. A verified user with NO organization
 * but pending invitations lands here BEFORE create-organization: they choose
 * explicitly — accept an invite (join that org and finish onboarding) or
 * decline. Declining the last invitation forwards to create-organization.
 * Without this step, an org-less user whose invites arrived after sign-up
 * would be funnelled into creating a duplicate solo org.
 */
export const Route = createFileRoute('/_authed/onboarding/invitations')({
  beforeLoad: async ({ context }) => {
    // Unverified users can't act on email-matched invites (the API refuses) —
    // send them to the verify step first.
    const session = await context.queryClient.ensureQueryData(sessionQueryOptions());
    if (!session?.user.emailVerified) {
      throw redirect({ to: '/onboarding/verify-email' });
    }
    // Users who already belong to an org don't onboard here — the signed-in
    // PendingInvitationsPrompt is their invite surface.
    const { organization } = await context.queryClient.ensureQueryData(
      currentOrganizationQueryOptions(),
    );
    if (organization) {
      throw redirect({ to: await resolveLandingRoute(context.queryClient) });
    }
    // Nothing pending → straight to create-organization.
    const { invitations } = await context.queryClient.ensureQueryData(
      myInvitationsQueryOptions(),
    );
    if (invitations.length === 0) {
      throw redirect({ to: '/onboarding/create-organization' });
    }
  },
  component: InvitationsRoute,
});

function InvitationsRoute(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data } = useQuery(myInvitationsQueryOptions());
  const acceptInvitation = useAcceptInvitation();
  const declineInvitation = useDeclineInvitation();

  const [busy, setBusy] = useState<{ orgId: string; action: 'accept' | 'decline' } | null>(null);
  const [error, setError] = useState('');

  const invitations = data?.invitations ?? [];

  async function handleAccept(organizationId: string): Promise<void> {
    triggerHaptic();
    setError('');
    setBusy({ orgId: organizationId, action: 'accept' });
    try {
      await acceptInvitation.mutateAsync({ body: { organizationId } });
      // The caller now belongs to (and is active in) the accepted org. REMOVE
      // the session + org + portfolio caches (not just invalidate) so the
      // `_protected` gate — which reads them via `ensureQueryData` with no
      // active observer — resolves the fresh membership instead of the stale
      // "no org" value (mirrors create-organization).
      queryClient.removeQueries({ queryKey: sessionQueryKey });
      queryClient.removeQueries({ queryKey: [...currentOrganizationQueryKey] });
      queryClient.removeQueries({ queryKey: [...myOrganizationsQueryKey] });
      queryClient.removeQueries({ queryKey: [...myInvitationsQueryKey] });
      queryClient.removeQueries({ queryKey: [...PORTFOLIO_KEY] });
      // Onboarding is complete — raise the "You're all set" celebration on the
      // landing surface.
      markOnboardingComplete();
      void navigate({ to: await resolveLandingRoute(queryClient), replace: true });
      // Leave the row busy — the navigation unmounts this step.
    } catch (err) {
      const wrapped = err instanceof Error ? err : new Error(String(err));
      errorTracker.captureException(wrapped, { source: 'onboardingInvitationAccept' });
      setError(t('invitations.acceptError'));
      setBusy(null);
    }
  }

  async function handleDecline(organizationId: string): Promise<void> {
    triggerHaptic();
    setError('');
    setBusy({ orgId: organizationId, action: 'decline' });
    try {
      const remaining = await declineInvitation.mutateAsync({ body: { organizationId } });
      // Write the returned remainder into the cache so the list (and the
      // create-organization beforeLoad's invite check) sees it immediately.
      queryClient.setQueryData([...myInvitationsQueryKey], remaining);
      if (remaining.invitations.length === 0) {
        void navigate({ to: '/onboarding/create-organization', replace: true });
        return;
      }
      setBusy(null);
    } catch (err) {
      const wrapped = err instanceof Error ? err : new Error(String(err));
      errorTracker.captureException(wrapped, { source: 'onboardingInvitationDecline' });
      setError(t('invitations.declineError'));
      setBusy(null);
    }
  }

  function busyActionFor(organizationId: string): InvitationBusyAction {
    return busy?.orgId === organizationId ? busy.action : null;
  }

  return (
    <Reveal variant="fade">
      <Stack gap="lg" data-testid="invitations-step">
        <OnboardingHeader
          mark={
            <div className={styles.medallion}>
              <Mail size={32} weight="light" />
            </div>
          }
          title={t('invitations.title')}
          subtitle={t('invitations.subtitle')}
        />

        <Stack gap="sm">
          {invitations.map((invitation) => (
            <InvitationRow
              key={invitation.organizationId}
              invitation={invitation}
              busyAction={busyActionFor(invitation.organizationId)}
              disabled={busy !== null}
              onAccept={() => void handleAccept(invitation.organizationId)}
              onDecline={() => void handleDecline(invitation.organizationId)}
            />
          ))}
        </Stack>

        {error !== '' ? (
          <Text size="sm" c="red">
            {error}
          </Text>
        ) : null}
      </Stack>
    </Reveal>
  );
}
